// data/projectsData.js

// Import all project logos
import AudioAnalyzerLogo from "../images/projects/Audio_Analyzer_Logo.png";
import AwayFromHomeLogo from "../images/projects/AwayFromHome_Logo.png";
import MileageLogo from "../images/projects/Mileage_Logo.png";
import QHelperLogo from "../images/projects/QHelper_Logo.png";
import QuizzerLogo from "../images/projects/Quizzer_Logo.png";
import QuantifyingHappinessLogo from "../images/projects/Quantifying_Happiness_Logo.png";

// Import language icons
import CIcon from "../images/languages/C.png";
import SwiftIcon from "../images/languages/Swift.png";
import PythonIcon from "../images/languages/Python.png";

// Import screenshots
import AudioAnalyzer1 from "../images/projects/audio_analyzer/audio_analyzer1.gif";

import AwayFromHome1 from "../images/projects/awayfromhome/awayfromhome1.png";
import AwayFromHome2 from "../images/projects/awayfromhome/awayfromhome2.png";
import AwayFromHome3 from "../images/projects/awayfromhome/awayfromhome3.png";
import AwayFromHome4 from "../images/projects/awayfromhome/awayfromhome4.png";

import Mileage1 from "../images/projects/mileage/mileage1.jpeg";
import Mileage2 from "../images/projects/mileage/mileage2.jpeg";

import QHelper1 from "../images/projects/qhelper/qhelper1.png";
import QHelper2 from "../images/projects/qhelper/qhelper2.png";
import QHelper3 from "../images/projects/qhelper/qhelper3.png";
import QHelper4 from "../images/projects/qhelper/qhelper4.png";

import Quizzer1 from "../images/projects/quizzer/quizzer1.png";
import Quizzer2 from "../images/projects/quizzer/quizzer2.png";
import Quizzer3 from "../images/projects/quizzer/quizzer3.png";
import Quizzer4 from "../images/projects/quizzer/quizzer4.png";
import Quizzer5 from "../images/projects/quizzer/quizzer5.png";

import Happiness1 from "../images/projects/happiness/happiness1.png";
import Happiness2 from "../images/projects/happiness/happiness2.png";
import Happiness3 from "../images/projects/happiness/happiness3.png";

// Create a mapping for language icons
export const languageIcons = {
  C: CIcon,
  Swift: SwiftIcon,
  Python: PythonIcon,
};

export const projects = {
  quantifyingHappiness: {
    name: "Quantifying Happiness",
    // Short description for listing page preview
    description:
      "iOS app that models the relationship between daily health metrics and user satisfaction using Simulated Annealing and k-Nearest Neighbors to provide personalized lifestyle recommendations.",
    gitHubRef: "https://github.com/aefremenko24/quantifying-happiness",
    logoRef: QuantifyingHappinessLogo,
    logoAlt: "Quantifying Happiness app logo",
    languages: ["Python", "Swift"],
    screenshots: [
      {
        src: Happiness1,
        alt: "Quantifying Happiness app interface showing home screen",
      },
      {
        src: Happiness2,
        alt: "Quantifying Happiness app interface showing calendar view",
      },
      {
        src: Happiness3,
        alt: "Quantifying Happiness app interface showing day view",
      },
    ],
    // Full markdown content for the project page
    content: `
An iOS application that learns the relationship between your daily health metrics and self-reported satisfaction and suggests personalized behavior changes.

## Motivation

Wearable devices like Apple Watch and WHOOP band collect useful health metrics daily, but they typically use them to provide one-size-fits-all recommendations rather than personalized guidance. For example, existing wellness platforms like WHOOP, Oura, and FitBit estimate recovery scores and give vague guidance such as "increase sleep" or "reduce strain," but their recommendation engines aren't directly optimized to maximize the user's reported satisfaction.

## Our Solution

Rather than relying on population-based heuristics, our application tailors a personal model for each user based on their historical data.

## Technical Approach

Our application retrieves the following metrics from the user's HealthKit storage:

| Metric | Units |
|--------|-------------|
| Steps Taken | count |
| Time in Bed | minutes |
| Calories Burnt | count |
| Time Exercising | minutes |
| Stand Hours | count |
| Daylight Time | minutes |
| Distance Walked | meters |
| Flights Climbed | count |
| Resting Heart Rate | BPM |

The algorithm we chose to power the optimization of the satisfaction score based on health metrics is Simulated Annealing.

### Why Simulated Annealing?

The relationship between health metrics and satisfaction is non-linear with multiple local optima. For example, training data might show high satisfaction for both "high exercise, low sleep" and "moderate exercise, high sleep" combinations.

Simulated Annealing was chosen because it:

- Works immediately with any amount of data (users only accumulate 365 data points per year)
- Improves with more data without requiring algorithmic changes
- Escapes local optima through probabilistic acceptance
- Adapts to different landscapes via temperature, cooling rate, and step size parameters

### k-Nearest Neighbors Regression (KNN)

For the evaluation function, we use KNN regression to predict satisfaction scores for potentially unseen parameter combinations:

- Non-parametric: Makes no assumptions about the underlying functional form
- No training phase: Adding data simply means adding it to the database
- Natural bounds: Doesn't extrapolate outside training data range, preventing dangerous and unrealistic recommendations

## Results

Our testing demonstrated that the optimizer successfully:

1. Improved low satisfaction states from 5.0 to 8.98 in 1000 iterations
2. Found global optima even when starting from high-satisfaction states
3. Stayed within bounds by never predicting satisfaction above the maximum in training data (9.0)

## Team

This project was developed as a CS 4100 (Artificial Intelligence) final project at Northeastern University by Arthur Efremenko, Ben Shainman, Patrick Flanagan, and Cevdet Isik.

## Future Directions

- Collecting more real-world labeled satisfaction data
- Implementing reinforcement learning for personal reward structures
- Adding random restarts and genetic algorithms
- Exploring alternative regression models for the objective function
`,
  },

  audioAnalyzer: {
    name: "Audio Analyzer",
    // Short description for listing page preview
    description:
      "Cross-platform digital sound analyzer application based on the PulseAudio sound server. Sound input is collected from a chosen source and metrics, including volume and frequencies, are displayed in the console. Supports Linux (using ALSA) and MacOS (using CoreAudio).",
    gitHubRef: "https://github.com/aefremenko24/audio_analyzer_c",
    logoRef: AudioAnalyzerLogo,
    logoAlt: "App logo with a waveform icon",
    languages: ["C"],
    screenshots: [
      {
        src: AudioAnalyzer1,
        alt: "Demo gif of the Audio Analyzer app showing real-time audio visualization",
      },
    ],
    // Full markdown content for the project page
    content: `
Cross-platform digital sound analyzer application written in C using the PulseAudio sound server. Sound input is collected from a chosen input and metrics, including volume, frequencies, and live waveform are displayed in the console. Currently supports Linux (using ALSA) and MacOS (using CoreAudio).

## Features

- Real-time audio analysis with live frequency spectrum visualization
- Cross-platform support for Linux (ALSA) and macOS (CoreAudio)
- Frequency spectrum display using Fast Fourier Transform (FFT)
- Volume metering with peak detection
- Ability to pipe the audio from any input into any output with automatic channel matching

## Technical Details

The program interfaces directly with the operating system's audio subsystem:

- Linux: Uses ALSA (Advanced Linux Sound Architecture) for low-level audio capture
- macOS: Uses the CoreAudio framework for system audio access

### Architecture

\`\`\`
Audio Source → Buffer → FFT Processing → Visualization
                ↓
           Volume Calculation
\`\`\`

The audio processing pipeline captures samples at 44.1kHz, processes the buffer in real-time, and outputs both frequency data and volume levels to the console.

## Getting Started

These instructions will get you a copy of the project up and running on your local machine for development and testing purposes.

### Prerequisites

NCURSES is used to display the textual user interface (TUI) of this program. The library is preinstalled on most machines. However, if you are having trouble importing the library, make sure that it is installed on your machine by running the appropriate command in your terminal:

- macOS: \`brew install ncurses\`
- Linux: \`sudo apt install libncurses-dev\`

### Making the project

To run audio_analyzer for the first time, run \`make install-deps\` to install the dependencies required for this project. Some of the libraries require \`sudo\` for their installation, so you will likely be prompted to enter your password.

Once the dependencies are installed, run \`make\` to compile the project.

### Running audio_analyzer

Once the project is compiled and all prerequisites are completed, run the \`audio_analyzer\` file populated in the repo-level directory and follow the prompts in the terminal.

## Built With

* [PulseAudio](https://www.freedesktop.org/wiki/Software/PulseAudio/) - Sound Server used to capture sound signals
* [NCURSES](https://tldp.org/HOWTO/NCURSES-Programming-HOWTO/) - Library used to display the TUI
* [FFTW](https://www.fftw.org/) - Software used to analyze the frequencies of the signal.

## Authors

* **Arthur Efremenko** - *Initial work* - [GitHub](https://github.com/aefremenko24)
`,
  },

  awayFromHome: {
    name: "Away from Home",
    description:
      "A social network focused on providing resources to people in need, connecting individuals with essential services and community support.",
    gitHubRef: "https://github.com/aefremenko24/AwayFromHome",
    logoRef: AwayFromHomeLogo,
    logoAlt:
      "Away from Home app logo with a white bird flying away drawing on a black background",
    languages: ["Swift"],
    screenshots: [
      {
        src: AwayFromHome1,
        alt: "Away from Home app interface showing home screen",
      },
      {
        src: AwayFromHome2,
        alt: "Away from Home app interface showing resource listings",
      },
      {
        src: AwayFromHome3,
        alt: "Away from Home app interface showing user profile",
      },
      {
        src: AwayFromHome4,
        alt: "Away from Home app interface showing settings",
      },
    ],
    content: `
*Swift Student Challenge 2025 Entry*

A social network focused on providing resources to people in need, connecting individuals with essential services and community support.

## Mission

Away from Home was created to bridge the gap between people experiencing hardship and the resources available to help them. The app provides a platform for:

- Finding local resources such as shelters, food banks, and medical services
- Community connections to share information and support
- Community resource verification by to ensure listed services are accurate and available

## Implementation

Built natively for iOS using Swift and SwiftUI, with:
- Google Cloud Firestore used for data storage
- Google Firebase used for user authentication 
- SwiftData for local data storage
`,
  },

  mileage: {
    name: "Lease Mileage Display",
    description:
      "A simple, focused app that helps you track your car lease mileage and avoid overage fees.",
    gitHubRef: "https://github.com/aefremenko24/lease_mileage_calculator",
    logoRef: MileageLogo,
    logoAlt: "Logo of an app with a red animated car",
    languages: ["Swift"],
    screenshots: [
      {
        src: Mileage1,
        alt: "Mileage Calculator app showing current mileage status",
      },
      {
        src: Mileage2,
        alt: "Mileage Calculator app displaying lease details and calculations",
      },
    ],
    content: `
A simple, focused app that helps you track your car lease mileage and avoid overage fees.

## Motivation

Car leases typically include a mileage limit (e.g., 10,000, 12,000, or 15,000 miles per year). Going over this limit can result in expensive penalties, usually 0.15-0.25 dollars per mile. 

Many people don't realize they're over their limit until the lease ends, resulting in unexpected charges of hundreds or even thousands of dollars.

## Solution

Lease Mileage Display calculates exactly the number of miles you be at today based on your total allowed mileage for the lease term and your lease start date.

## Features

- Clean, glanceable interface: See your status at a glance
- Widget support: Check your status without opening the app
- Multiple vehicles: Track more than one lease

### Calculation Formula

\`\`\`
Daily Allowance = Total Miles / Lease Days
Target Today = Daily Allowance x Days Elapsed
\`\`\`
`,
  },

  qhelper: {
    name: "QHelper",
    description:
      "MacOS application for parsing Northeastern University Lighting Cues Sheets (in XLSX format) and adding time stamps for all groups to a given QLab workspace as cues of a selected type. Allows for customization of grouping to fit the work style of the lighting engineer.",
    gitHubRef: "https://github.com/aefremenko24/qhelper",
    logoRef: QHelperLogo,
    logoAlt: "QHelper app logo",
    languages: ["Swift"],
    screenshots: [
      {
        src: QHelper1,
        alt: "QHelper app screenshot 1 showing file selection interface",
      },
      {
        src: QHelper2,
        alt: "QHelper app screenshot 2 showing timestamp parsing",
      },
      {
        src: QHelper3,
        alt: "QHelper app screenshot 3 showing cue configuration",
      },
      {
        src: QHelper4,
        alt: "QHelper app screenshot 4 showing QLab integration",
      },
    ],
    content: `
MacOS application for parsing Northeastern University Lighting Cues Sheets (in XLSX format) and adding time stamps for all groups to a given QLab workspace as cues of a selected type. Allows for customization of grouping to fit the work style of the lighting engineer. 

## Getting Started

These instructions will get you a copy of the project up and running on your local machine for development and testing purposes.

### Prerequisites

* This project requires a MacOS version 10.13 or later.
* To debug and test this project, XCode IDE version 16.0 or later should be used.
* The latest version of the Command Line Developer Tools must be installed on the Mac running the project.  
* Make sure QLab is installed on the machine running QHelper and a workspace is open.

### Building and running QHelper

Once the project is cloned and all prerequisites are satisfied, go to the **Product** tab in XCode and press **Run** to build and run the project. 

### Permissions

QHelper will require some user permission to function properly:
* **Network: Incoming Connections (Server)** - Receive responses from QLab (primarily used to get unique cue IDs).
* **Network: Outgoing Connections (Client)** - Send messages to QLab. Used for adding cues, specifying their parameters, and arranging cue groups.
* **File Access: User Selected File (Read Only)** - Parse XLSX files added by the user.
* **Resourse Access: Apple Events** - Run Apple Scripts to convert generic MIDI cues into GIO commands. 

## Built With

* [CoreXLSX](https://github.com/CoreOffice/CoreXLSX) - Excel spreadsheet (XLSX) format parser.
* [OSCKit](https://tldp.org/HOWTO/NCURSES-Programming-HOWTO/) - Open Sound Control (OSC) library.

## Authors

* **Arthur Efremenko** - *Initial work* - [GitHub](https://github.com/aefremenko24)
`,
  },

  quizzer: {
    name: "Quizzer",
    description:
      "A simple study tool for creating and practicing multiple choice questions.",
    gitHubRef: "https://github.com/aefremenko24/Quizzer",
    logoRef: QuizzerLogo,
    logoAlt:
      "Quizzer app logo resembling a scantron bubble filled in with a #2 pencil",
    languages: ["Swift"],
    screenshots: [
      {
        src: Quizzer1,
        alt: "Quizzer app screenshot 1 displaying quiz selection screen",
      },
      {
        src: Quizzer2,
        alt: "Quizzer app screenshot 2 displaying question creation interface",
      },
      {
        src: Quizzer3,
        alt: "Quizzer app screenshot 3 displaying active quiz taking",
      },
      {
        src: Quizzer4,
        alt: "Quizzer app screenshot 4 displaying results page",
      },
      {
        src: Quizzer5,
        alt: "Quizzer app screenshot 5 displaying study mode",
      },
    ],
    content: `
A simple study tool for creating and practicing multiple choice questions.

## Features

### Create Quizzes
- Build custom question sets with up to 26 answer choices
- Organize quizzes by subject or topic
- Import questions from .quizzer files (see format description below)

### Export and Share Quizzes
- Export quizzes as shareable .quizzer files
- Import quizzes from other Quizzer users
- Cloud sync across your devices

### Progress Tracking
- View accuracy statistics by study set
- Track improvement over time
- Identify weak areas that need more practice

## .quizzer File Format

The .quizzer file format is used for exporting and sharing study sets. It is very trivially designed and is simular to a comma separated format, where the element separator is /~/ and the row separator is \\r or \\n. 
`,
  },
};
